import {
  ArpMedia as ArpMediaInterface,
  ArpMediaType,
  User as IUser,
  PaginatedResponse,
  QueryParams
} from '@arpeggi/shared'
import { types, unprotect } from 'mobx-state-tree'
import { GetFavoritesParams, getUserFavorites } from '../../__legacy__/api/v2/ArpFavoritesAPI'
import { GetArpMediaParams, getArpMedia, getOneArpMedia } from '../../__legacy__/api/v2/ArpMediaAPI'
import axios from '../../__legacy__/lib/axios'
import { ForDirectExtend } from '../mobx'
import ArpMedia, { IArpMedia } from './ArpMedia'
import User from './User'

// this is inclusive -- this includes the original 407 samples,
// but best to use the endpoint that pulls default samples
// as that will include every default sample
export const MAX_DEFAULT_SAMPLE_INDEX = 406

const LIMIT_PER_PAGE = 30

const BLANKDATA: PaginatedResponse<IArpMedia> = {
  meta: {
    total: 0,
    perPage: 0,
    currentPage: 1,
    lastPage: 1,
    firstPage: 1,
    firstPageUrl: '',
    lastPageUrl: '',
    nextPageUrl: '',
    previousPageUrl: ''
  },
  data: []
}

/**
 *
 * @param int string that potentially contains an id inside
 * @returns A boolean value that will indicate whether this is a valid arp id
 */
const isValidArpInteger = (int: string) => {
  const num = parseInt(int)
  if (!isFinite(num)) return false
  if (num !== Math.floor(num)) return false
  if (num < 0) return false
  return true
}

const createPromiseStore = <K = string, V = any>() => new Map<K, Promise<V>>()

const _ArpRegistry = types
  .model({
    cache: types.map(ArpMedia),
    users: types.map(User),

    // this means that all samples have been loaded and all uploaded stems have been loaded
    // so the studio can be used without having to await anything
    readyForStudioUse: false
  })
  .extend((self) => {
    // the IDs of all stems that were uploaded by users (used in the studio)
    let uploadedStemIDs: number[] = []
    /**
     * promises are stored to ensure that no duplicate requests are being called at the same time
     */
    const singlePromiseStore = createPromiseStore<string, IArpMedia>()
    const manyPromiseStore = createPromiseStore<string, IArpMedia[]>()

    function afterCreate() {
      unprotect(self)
    }

    function _setUser(user: IUser) {
      self.users.set(user.walletAddress, user)
    }

    /**
     * Sets item to local cache
     * @param item ArpMedia object, see ArpMedia in @arpeggi/shared
     */
    function _setMediaById(item: ArpMediaInterface) {
      const { subComponents, ...media } = item
      const _id = media.id.toString()

      let processedMedia = { subComponents: undefined, ...media }

      // settings subcomponents if we have an array
      if (subComponents) {
        // add subcomponents array of ids
        const subcomponentIDs = subComponents.map((m) => {
          _setMediaById(m)
          return m.id
        })
        processedMedia = { ...processedMedia, subComponents: subcomponentIDs }
      }

      if (processedMedia.access === null) processedMedia.access = undefined
      if (processedMedia.tokenGate === null) processedMedia.tokenGate = undefined

      // @ts-ignore
      self.cache.set(_id, processedMedia) // TODO: FIX THAT SHIT, TYPESCRIPT CONFIGS DEVIATE, SO OPTIONAL VALUES ARE REQUIRED in mobx but not in @arpeggi/shared
      // todo fix fetching subComponents on api/v2, currently it doesn't return user object
      // subComponents.forEach((m) => _setMediaById(m))
    }

    function setMediaById(item: ArpMediaInterface) {
      return _setMediaById(item)
    }

    /**
     *
     * @param id Arp index string
     * @returns ArpMedia mobx object or undefined depending on whether data was prefetched or not
     */
    function _getMediaById(id: string): IArpMedia | undefined {
      return self.cache.get(id)
    }

    /**
     * A light wrapper around _getMediaById that can take in a number as input as well
     * @param id Arp index as number or string
     * @returns ArpMedia mobx object or undefined depending on whether data was prefetched or not
     */
    function retrieveFromCache(id: number | string) {
      // format to string and validate
      const _id = id.toString()
      if (!isValidArpInteger(_id)) throw new Error('Error parsing id.')
      return _getMediaById(_id)
    }

    /**
     * Fetches and caches arp media, stores additional arp media which may be linked as subComponents array
     * @param id Arp media id
     * @returns Parsed and cached ArpMedia mobx instance
     */
    async function _fetchById(id: string): Promise<IArpMedia> {
      const media = await getOneArpMedia(id, {
        parts: ['subComponents', 'favoriteCount']
      })
      if (!media) throw new Error('no data was returned from api')
      _setMediaById(media)
      return _getMediaById(id)
    }

    /**
     * An override function that manually caches a list of ArpMedias.
     * Returns the corresponding IArpMedia types for those objects.
     * Helpful when pulling data directly from the server.
     * @param items ArpMedia[] object, see ArpMedia in @arpeggi/shared
     */
    function setMedias(items: ArpMediaInterface[]): IArpMedia[] {
      let returnedMedia = []
      items.forEach((item) => {
        const id = item.id.toString()
        let currentlyCached = _getMediaById(id)
        if (!currentlyCached) {
          _setMediaById(item)
          currentlyCached = _getMediaById(id)
        }
        returnedMedia.push(currentlyCached)
      })
      return returnedMedia
    }

    /**
     * Fetches single piece of ArpMedia. In case of
     * @param id Arp index. can be either number or string
     * @returns Parsed and cached ArpMedia mobx instance
     */
    async function getByIdAsync(id: number | string, force = false) {
      try {
        // format to string and validate
        const _id = id.toString()
        if (!isValidArpInteger(_id)) throw new Error('Error parsing id.')

        // return instance if already cached
        const cachedMedia = _getMediaById(_id)
        if (!force && cachedMedia) return cachedMedia

        // request data from api if it hasn't been requested yet and return it
        if (!singlePromiseStore.has(_id)) singlePromiseStore.set(_id, _fetchById(_id))
        const media = await singlePromiseStore.get(_id)
        singlePromiseStore.delete(_id)
        return media
      } catch (err) {
        // in case of errors just return undefined
        console.error(err)
        return undefined
      }
    }

    /**
     * Fetches many pieces of ArpMedia. In case of
     * @param ids Arp indexes. can be either number or string
     * @returns Parsed and cached ArpMedia mobx instances
     */
    async function getByIdsAsync(ids: (number | string)[]) {
      return await Promise.all(ids.map((id) => getByIdAsync(id)))
    }

    /**
     *
     * @param params GetArpMediaParams. Includes sorting, filtering e.t.c.
     * @returns a list of arp media mobx instances + metadata for pagination
     */
    async function search(params: GetArpMediaParams = {}) {
      const getParams: GetArpMediaParams = {
        orderBy: 'mintedAt',
        order: 'desc',
        parts: ['subComponents', 'favoriteCount'],
        perPage: LIMIT_PER_PAGE,
        shadowBlocklist: true,
        ...params
      }
      if (getParams.subComponentId && getParams.subComponentId.length === 0)
        return { media: [], meta: BLANKDATA.meta }
      const { data, meta } = await getArpMedia(getParams)
      const media = data.map((m) => {
        _setMediaById(m)
        return _getMediaById(m.id.toString())
      })
      return { meta, media }
    }

    async function getFavoritesByWalletAddress(walletAddress: string, params: GetFavoritesParams) {
      const getParams: GetFavoritesParams = {
        order: 'desc',
        ...params
      }
      const { data, meta } = await getUserFavorites(walletAddress, getParams)
      const media = data.map((m) => {
        _setMediaById(m)
        return _getMediaById(m.id.toString())
      })
      return { meta, media }
    }

    /**
     * Returns all stems that have no arpeggi composition, and thus should be treated
     * more like samples
     */
    async function getUploadedStemsAsync(): Promise<IArpMedia[]> {
      if (uploadedStemIDs.length === 0) {
        const { data } = await axios.get<ArpMediaInterface[]>('/api/v2/arp-media/uploaded-stems')
        uploadedStemIDs = data.map((item) => item.id)
        return setMedias(data)
      }
      return uploadedStemIDs.map((ID) => retrieveFromCache(ID))
    }

    /**
     * Async loads all of the samples
     */
    async function getAllSamplesAsync(): Promise<IArpMedia[]> {
      const requestParams: GetArpMediaParams = {
        [QueryParams.PER_PAGE]: 1000,
        [QueryParams.ORDER]: 'desc',
        type: [ArpMediaType.SAMPLE]
      }
      const resp = await axios.get<PaginatedResponse<ArpMediaInterface>>('/api/v2/arp-media', {
        params: requestParams
      })
      if (!resp.data) {
        throw new Error('[registry] error fetching all')
      }
      return setMedias(resp.data.data)
    }

    /** Called once all of the samples have been loaded so the studio can be used seamlessly */
    function setReadyForStudioUse() {
      self.readyForStudioUse = true
    }

    return {
      views: {
        /** Returns all of the uploaded stems reactively */
        getUploadedStemsReactive(): IArpMedia[] {
          return uploadedStemIDs
            .map((index) => self.cache.get(index.toString()))
            .filter((stem) => stem)
        },
        /** Returns all cached arp medias with type "sample" */
        getAllSamples(): IArpMedia[] {
          return Array.from(self.cache.values()).filter((item) => item.type === ArpMediaType.SAMPLE)
        },
        /** Returns a random arp ID for an already cached sample */
        getRandomSampleId(): number {
          const indexes = Array.from(self.cache.values())
            .filter((item) => item.type === ArpMediaType.SAMPLE)
            .map((item) => item.id)
          return indexes[Math.floor(Math.random() * indexes.length)]
        }
      },
      actions: {
        afterCreate,
        setMedias,
        getAllSamplesAsync,
        getByIdAsync,
        getByIdsAsync,
        search,
        setMediaById,
        getFavoritesByWalletAddress,
        getUploadedStemsAsync,
        setReadyForStudioUse,
        retrieveFromCache
      }
    }
  })

interface ArpRegistryType extends ForDirectExtend<typeof _ArpRegistry> {}
const ArpRegistry: ArpRegistryType = _ArpRegistry

export default ArpRegistry.create()
